import { useEffect, ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RefundTransactionInfo from './RefundTransactionInfo';
import useFetchEFTFundTransaction from '@/hooks/useApi/useFetchEFTFundTransaction';
import useFetchEFTWithdrawTransaction from '@/hooks/useApi/useFetchEFTWithdrawTransaction';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import LinkedTransactionInfo from './LinkedTransactionInfo';
import { EftTransaction, LinkedTransactionsMap } from 'vopay';

type TransactionType = EftTransaction & {
  ParentTransaction?: LinkedTransactionsMap;
  SubTransactions?: LinkedTransactionsMap;
};

type RefundTransactionContainerProps = {
  TransactionID: string;
  isParent?: boolean;
} & Omit<ComponentProps<typeof RefundTransactionInfo>, 'transaction' | 'isParent'>;

export default function RefundTransactionContainer({
  TransactionID,
  isParent = false,
  jwt,
  ...props
}: RefundTransactionContainerProps) {
  const { userSettings } = useUserSettingsProvider();
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { eftFundTransaction, fetchEftFundTransaction } = useFetchEFTFundTransaction({ bearer: jwt });
  const { eftWithdrawTransaction, fetchEftWithdrawTransaction } = useFetchEFTWithdrawTransaction({ bearer: jwt });
  const [transaction, setTransaction] = useState<TransactionType | null>(null);

  const parentTransactions = eftWithdrawTransaction?.ParentTransaction
    ? Object.values(eftWithdrawTransaction.ParentTransaction)
    : undefined;

  const subTransactions = eftFundTransaction?.SubTransactions
    ? Object.values(eftFundTransaction.SubTransactions)
    : undefined;

  useEffect(() => {
    blockingLoader.show();

    // A refund resolves as either a fund or a withdrawal; the rail it isn't throws (Success:false),
    // so allSettled lets the winning rail resolve without the losing one's throw rejecting the pair.
    Promise.allSettled([
      fetchEftFundTransaction({ TransactionID, Timezone: userSettings.timezone }),
      fetchEftWithdrawTransaction({ TransactionID, Timezone: userSettings.timezone }),
    ])
      .then(([fundResult, withdrawResult]) => {
        if (fundResult.status === 'fulfilled') {
          setTransaction(fundResult.value);
        } else if (withdrawResult.status === 'fulfilled') {
          setTransaction(withdrawResult.value);
        } else {
          // Neither rail resolved: surface the failure instead of a blank panel.
          setTransaction(null);

          alertDialog.error(
            tGlobal('error'),
            fundResult.reason instanceof Error ? fundResult.reason.message : tGlobal('errorOccurred'),
          );
        }
      })
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  return (
    <div>
      {transaction && <RefundTransactionInfo transaction={transaction} isParent={isParent} jwt={jwt} {...props} />}

      {parentTransactions && parentTransactions.length > 0 && (
        <LinkedTransactionInfo
          LinkedTransactions={parentTransactions}
          Currency={eftWithdrawTransaction?.Currency || 'CAD'}
        />
      )}

      {subTransactions && subTransactions.length > 0 && (
        <LinkedTransactionInfo
          LinkedTransactions={subTransactions}
          Currency={eftWithdrawTransaction?.Currency || 'CAD'}
        />
      )}
    </div>
  );
}
